import {
    Box, Chip, FormControl, Grid, InputLabel, MenuItem, OutlinedInput,
    Select,
    SelectChangeEvent,
    TextField
} from "@mui/material";
import { Dispatch, FC, Fragment, useEffect, useState } from "react";
import { IBu, MenuProps } from "../../content/parametrage/Bu/Bu";
import { IPays } from "../../content/parametrage/Pays/Pays";
import { LoadingButton } from "@mui/lab";
import AddIcon from '@mui/icons-material/Add';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CheckAttr from "../../../utils/CheckAttr";
import { AxiosInstance } from "axios";
import { AlertType } from "../../../types/AlertType";

interface BusinessUnitForm {
    allPays: IPays[];
    http: AxiosInstance;
    rowBu?: IBu;
    onClose: () => void;
    reloadList: () => void;
    setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
    setAlerteMessage: Dispatch<React.SetStateAction<string>>;
    setAlertType: Dispatch<React.SetStateAction<AlertType>>;
}

interface InputValidation {
    pays: boolean;
    libelle: boolean;
}

const resetInpuValidation: InputValidation = {
    pays: false,
    libelle: false,
};

const BusinessUnitForm: FC<BusinessUnitForm> = ({
    allPays,
    http,
    rowBu,
    onClose,
    reloadList,
    setOpenSnack,
    setAlerteMessage,
    setAlertType
}) => {
    const [isClickSave, setIsClickSave] = useState<boolean>(false);
    const [openSelectMultiple, setOpenSelectMultiple] = useState<boolean>(false);
    const [paysToSave, setpaysToSave] = useState<string[]>([]);
    const [libelleAjouter, setLibelleAjoute] = useState<string>("");
    const [errorInput, setErrorInput] = useState<InputValidation>(resetInpuValidation);

    useEffect(() => {
        if (rowBu) {
            const pays = rowBu.pays.reduce((pLibelle, pTemp) => {
                pLibelle.push(`${pTemp.id} - ${pTemp.libelle}`)
                return pLibelle
            }, [] as string[]);

            setpaysToSave(pays);
            setLibelleAjoute(rowBu?.libelle);
        }
    }, []);

    const transformValue = (id: number): string => {
        const pays = allPays?.find((elem) => elem?.id === id);

        return `${pays?.id} - ${pays?.libelle}`;
    };

    const handleChange = (event: SelectChangeEvent<typeof paysToSave>) => {
        const { target: { value } } = event;
        setpaysToSave(
            // On autofill we get a stringified value.
            typeof value === "string" ? value.split(",") : value
        );
    };

    const handleCloseSelectMulti = () => {
        setOpenSelectMultiple((prev) => !prev);
    };

    const isValidate = (): boolean => {
        // Reinitialise les variables des validations
        setErrorInput(resetInpuValidation);

        if (!CheckAttr(paysToSave) || !CheckAttr(libelleAjouter)) {
            if (!CheckAttr(paysToSave)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, pays: true }));
            }

            if (!CheckAttr(libelleAjouter)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, libelle: true }));
            }

            setAlerteMessage("Merci de remplir les champs en rouge !");
            setAlertType("warning");
            setOpenSnack(true);

            return false;
        }
        return true;
    }

    const handleSave = () => {
        if (isValidate()) {
            setIsClickSave(true);
            const data = {
                libelle: libelleAjouter,
                dataAsso: paysToSave,
            };
            if (rowBu) {
                http.post(`update/bu/${rowBu.id}`, data)
                    .then(() => {
                        setAlerteMessage("Mis à jour efféctuée !");
                        setAlertType("success");
                        setOpenSnack(true);
                        onClose();
                        reloadList();
                    }).catch(error => {
                        console.log(error);
                    }).finally(() => {
                        setIsClickSave(false);
                    });
            } else {
                http.post('create/bu', data)
                    .then(() => {
                        setAlerteMessage("Enregistrement efféctuée !");
                        setAlertType("success");
                        setOpenSnack(true);
                        onClose();
                        reloadList();
                    }).catch(error => {
                        console.log(error);
                    }).finally(() => {
                        setIsClickSave(false);
                    });
            }
        }
    };

    return (
        <Fragment>
            <Grid container spacing={2} columns={{ xs: 6, sm: 6, md: 6 }}>
                <Grid item xs={6} sm={6} md={6}>
                    <FormControl fullWidth>
                        <InputLabel id="demo-multiple-chip-label" color="warning">
                            Pays
                        </InputLabel>
                        <Select
                            labelId="demo-multiple-chip-label"
                            id="demo-multiple-chip"
                            multiple
                            color="warning"
                            open={openSelectMultiple}
                            value={paysToSave}
                            error={errorInput?.pays}
                            onChange={handleChange}
                            onClick={() => handleCloseSelectMulti()}
                            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={value}
                                            color="warning"
                                        />
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {allPays.map((pays) => (
                                <MenuItem
                                    key={pays?.id}
                                    value={transformValue(pays?.id as number)}
                                >
                                    {pays?.id} - {pays?.libelle}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={6} md={6}>
                    <TextField
                        label="Libelle"
                        color="warning"
                        fullWidth
                        error={errorInput.libelle}
                        value={libelleAjouter}
                        onChange={(e) => setLibelleAjoute(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
            </Grid>
            <Box display="flex" justifyContent="center" mt={5}>
                <LoadingButton
                    onClick={() => handleSave()}
                    endIcon={rowBu ? <BorderColorIcon /> : <AddIcon />}
                    loading={isClickSave}
                    loadingPosition="end"
                    sx={{ textTransform: "none" }}
                    variant="contained"
                    color="warning"
                >
                    {rowBu ? "Enregistrer" : "Ajouter"}
                </LoadingButton>
            </Box>
        </Fragment>
    );
};

export default BusinessUnitForm;